import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { DynamicReportService, IReportCategory } from "../../../_services/dynamicReports.service";
import { ColumnDataType } from "../../../enums/ColumnDataType";
import { ReportDataFunction } from "../../../enums/ReportDataFunction";
import { IColumnHeader } from "../../../models/IColumnHeader";
import "./tableColumnSelector.scss";

export interface ITableColumnSelectorComponentBindings {
    allColumns: IColumnHeader[];
    selectedColumns: IColumnHeader[];

    onColumnsChanged: (args: { columns: IColumnHeader[] }) => void;
}

class TableColumnSelectorComponentController implements angular.IComponentController, ITableColumnSelectorComponentBindings {
    allColumns: IColumnHeader[] = [];
    selectedColumns: IColumnHeader[] = [];
    dataFunctions = ReportDataFunction;

    //reportDefinition: IReportDefinition<any>;
    onColumnsChanged: (args: { columns: IColumnHeader[] }) => void;
    columnsHaveChanged = false;

    showAddColumnDialog: boolean;
    showColumnPicker: boolean;

    columnPickerLoading = false;

    columnToEdit: IColumnHeader;

    canFilterColumn: boolean;
    showFilterValueOperators: boolean;
    showTimeFilter: boolean;

    maxColumns: number;
    filterTimeMinute: number;
    filterTimeHour: number;

    columnDataTypes = ColumnDataType;

    reportCategories: IReportCategory[];
    displayReportCategories: IReportCategory[];

    categoryFilterTitle = "All";

    static $inject = ["$timeout", "$mdDialog", "dynamicReportService"];

    constructor(
        private readonly $timeout: angular.ITimeoutService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly reportService: DynamicReportService) { }

    $onInit(): void {
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {

        if (!this.allColumns) {
            this.reportService.getDisplayColumnHeaders().then(headers => {
                this.allColumns = headers;

                this.$onChanges(null);
            });

            return;
        }

        if (this.selectedColumns) {
            this.selectedColumns = copy(this.selectedColumns);
            //this.updateSelectedDisplayColumns();
        }
    }

    $onDestroy() {
        if (this.showColumnPicker && this.columnsHaveChanged) {
            this.triggerOnColumnsChangedCallback();
        }
    }

    onSearch(searchString: string) {
        if (!searchString) {
            this.displayReportCategories = this.reportCategories;
            return;
        }

        const searchStringLower = searchString.toLocaleLowerCase();
        this.displayReportCategories = [];

        // Now search
        this.reportCategories.forEach(c => {
            var matchingReportColumns = c.columns.filter(c => c.Title.toLocaleLowerCase().indexOf(searchStringLower) >= 0);

            if (matchingReportColumns.length) {
                this.displayReportCategories.push({
                    title: c.title,
                    icon: c.icon,
                    columns: matchingReportColumns
                });
            }
        });
    }

    categoryFilterChanged() {

        if (this.categoryFilterTitle == "All") {
            this.displayReportCategories = this.reportCategories;
            return;
        }

        this.displayReportCategories = [];
        this.reportCategories.forEach(c => {
            if (c.title == this.categoryFilterTitle) {
                this.displayReportCategories.push(c);
            }
        });
    }

    editColumn(column: IColumnHeader) {
        if (this.columnToEdit === column) {
            this.columnToEdit = null;
            this.setColumnFilterOptions();
            return;
        }

        this.columnToEdit = column;
        this.setColumnFilterOptions();

        if (!column) {
            this.selectedColumnChanged();
        }
    }

    setColumnFilterOptions() {
        this.showTimeFilter = false;
        this.canFilterColumn = false;
        this.showFilterValueOperators = false;

        if (this.columnToEdit == null) {
            return;
        }

        if (this.columnToEdit.ColumnDefinition.Type === ColumnDataType.Duration) {
            this.showTimeFilter = true;
            const timeFilter = moment(this.columnToEdit.FilterValue, "HH:mm:ss");
            this.filterTimeHour = timeFilter.hour();
            this.filterTimeMinute = timeFilter.minute();
        }

        if (this.columnToEdit.ColumnDefinition.Type === ColumnDataType.Double ||
            this.columnToEdit.ColumnDefinition.Type === ColumnDataType.Int ||
            this.columnToEdit.ColumnDefinition.Type === ColumnDataType.Score ||
            this.columnToEdit.ColumnDefinition.Type === ColumnDataType.Percent ||
            this.columnToEdit.ColumnDefinition.Type === ColumnDataType.Duration) {
            this.canFilterColumn = true;
            this.showFilterValueOperators = true;

            if (!isNaN(Number(this.columnToEdit.FilterValue))) {
                this.columnToEdit.FilterValue = +this.columnToEdit.FilterValue;
            }

        }

        if (this.columnToEdit.ColumnDefinition.Type === ColumnDataType.String ||
            this.columnToEdit.ColumnDefinition.Type === ColumnDataType.List ||
            this.columnToEdit.ColumnDefinition.Type === ColumnDataType.Location) {
            this.canFilterColumn = true;
            this.showFilterValueOperators = false;
        }
    }

    showColumnPickerDialog() {
        this.showColumnPicker = true;

        // We reset this so subsequent dialog loads are also fast to transition
        this.reportCategories = [];
        this.displayReportCategories = [];

        // Creating the columns is expensive, so we delay by 300ms to allow any animations to finish first
        this.columnPickerLoading = true;
        this.$timeout(() => {

            this.reportCategories = this.reportService.getUniqueReportCategories(this.allColumns);
            this.displayReportCategories = this.reportCategories;

            //this.updateSelectedDisplayColumns();
            this.columnPickerLoading = false;
        }, 300);
    }

    hideColumnPickerDialog() {
        this.showColumnPicker = false;

        this.resetCategoryFilterTitle();
        this.selectedColumnChanged();
    }

    selectedColumnChanged() {
        this.updateSelectedDisplayColumns();
        this.columnsHaveChanged = true;

        this.triggerOnColumnsChangedCallback();
    }

    selectColumn(column: IColumnHeader): void {
        const columnCopy = copy(column);

        if (columnCopy.ColumnDefinition) {
            columnCopy.DataFunction = columnCopy.ColumnDefinition.DefaultReportDataFunction;
        }

        if (this.selectedColumns.length > this.maxColumns) {
            this.$mdDialog.show(this.$mdDialog.alert().title("Too Many Columns").textContent(`The maxuimn number of columns for this page orientation is ${this.maxColumns}`).ok("ok"));
            return;
        }

        this.selectedColumns.push(columnCopy);
        this.showAddColumnDialog = false;

        this.selectedColumnChanged();
    }

    columnSortOrderChanged(column: IColumnHeader) {
        this.selectedColumns.forEach(col => {
            col.Sorted = false;
        });

        column.Sorted = true;
        column.SortAscending = column.SortAscending as any === "true";

        this.selectedColumnChanged();
    }

    deselectColumn(column: IColumnHeader): void {
        const index = this.selectedColumns.indexOf(column);
        if (index >= 0) {
            this.selectedColumns.splice(index, 1);
        }

        this.selectedColumnChanged();
        this.editColumn(null);
    }

    onDragDrop(index: number) {
        this.selectedColumns.splice(index, 1);

        this.selectedColumnChanged();
    }

    onColumnDataFunctionChanged(column: IColumnHeader) {
        if (column.ColumnDefinition.DefaultReportDataFunction !== column.DataFunction) {
            column.Title = `${column.DefaultTitle} (${ReportDataFunction[column.DataFunction]})`;
        }
        else {
            column.Title = column.DefaultTitle;
        }

        this.selectedColumnChanged();
    }

    filterTimeChanged(hour: number, minute: number) {
        if (!this.columnToEdit) {
            return;
        }

        this.columnToEdit.FilterValue = moment().hour(hour).minute(minute).second(0).format("HH:mm:ss");
    }

    // ------------------------------------------------

    private resetCategoryFilterTitle(): void {
        this.categoryFilterTitle = "All";
    }

    private triggerOnColumnsChangedCallback() {

        if (this.onColumnsChanged) {
            this.onColumnsChanged({ columns: this.selectedColumns });
        }
    }

    private updateSelectedDisplayColumns() {
        if (!this.reportCategories) {
            return;
        }

        this.reportCategories.forEach(cat => {
            cat.columns.forEach(col => {
                (col as any).selected = this.selectedColumns.find(c => c.Title == col.Title) != null;
            });
        });
    }
}

export class TableColumnSelectorComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            allColumns: "<",
            selectedColumns: "<",
            maxColumns: "<",

            onColumnsChanged: "&"
        };

        this.controller = TableColumnSelectorComponentController;
        this.template = require("./tableColumnSelector.html");
    }
}
